import { Caption, Card, VAccordionContent, VAccordionItem, VAccordionTrigger, cn } from '@vrzl/ui';

import { GroupStatusIndicators } from '@/domains/information/components/groupStatusIndicators';

import type { ValidationFilter, ValidationGroupView } from '../utils/validationView';

import { StatusColumn, type StatusColumnVariant } from './statusColumn';

const COLUMNS: Record<ValidationFilter, StatusColumnVariant[]> = {
  all: ['found', 'missing'],
  present: ['found'],
  missing: ['missing'],
};

type ValidationGroupProps = {
  view: ValidationGroupView;
  filter: ValidationFilter;
  eventId: string;
  sentFeedback: ReadonlySet<string>;
  onFeedbackSent: (criterionId: string) => void;
};

/** Eén onderwerp: in- en uitklapbaar, met een of twee kolommen afhankelijk van het filter. */
export function ValidationGroup({
  view,
  filter,
  eventId,
  sentFeedback,
  onFeedbackSent,
}: ValidationGroupProps) {
  const { counts } = view;
  const headerCount =
    filter === 'missing'
      ? `${counts.missing} van ${counts.total} niet aanwezig`
      : `${counts.present} van ${counts.total} aanwezig`;

  return (
    <VAccordionItem value={view.key} className="border-b-0">
      <Card padding="none" className="overflow-hidden">
        <div className="relative">
          <VAccordionTrigger className="cursor-pointer items-center rounded-none px-5 py-4 text-base font-semibold text-foreground hover:no-underline focus-visible:ring-inset print:[&>svg]:hidden">
            <span className="flex flex-1 flex-wrap items-center gap-3">
              <span className="flex-1">{view.name}</span>
              <Caption as="span">{headerCount}</Caption>
              {/* Reserves the room of the indicators below, so the counts line up across groups. */}
              <span aria-hidden="true" className="w-16 shrink-0" />
            </span>
          </VAccordionTrigger>

          {/* Next to the trigger, not inside it: the indicators are buttons (no button in a button). */}
          <div className="absolute inset-y-0 right-14 flex items-center gap-1.5">
            <GroupStatusIndicators counts={counts} />
          </div>
        </div>
        <VAccordionContent className="px-5 pb-5">
          <div className={cn('grid grid-cols-1 gap-4', filter === 'all' && 'md:grid-cols-2')}>
            {COLUMNS[filter].map((variant) => (
              <StatusColumn
                key={variant}
                variant={variant}
                items={variant === 'found' ? view.found : view.missing}
                eventId={eventId}
                sentFeedback={sentFeedback}
                onFeedbackSent={onFeedbackSent}
              />
            ))}
          </div>
        </VAccordionContent>
      </Card>
    </VAccordionItem>
  );
}
